import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useGearCloset } from '../hooks/useGearCloset';

export function GearCloset() {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const { items, isLoading, error, removeItem } = useGearCloset();
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Redirect to login if not authenticated
  if (!authLoading && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Show loading while checking auth
  if (authLoading) {
    return (
      <main className="flex-1 px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse text-center py-12">
            <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-zinc-400">Loading...</p>
          </div>
        </div>
      </main>
    );
  }

  const handleRemove = async (itemId: string) => {
    if (removingId) return;

    setRemovingId(itemId);
    try {
      await removeItem(itemId);
    } catch {
      // Error handling could be improved
    } finally {
      setRemovingId(null);
    }
  };

  // Group items by category
  const groupedItems = items.reduce((acc, item) => {
    if (!acc[item.category_id]) {
      acc[item.category_id] = [];
    }
    acc[item.category_id].push(item);
    return acc;
  }, {} as Record<string, typeof items>);

  return (
    <main className="flex-1 px-4 sm:px-6 py-8 sm:py-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Your Gear Closet</h1>
          <p className="text-zinc-400">
            Track the gear you already own. Items you own will be marked when viewing kit recommendations.
          </p>
          {user?.email && (
            <p className="text-sm text-zinc-500 mt-2">
              Signed in as {user.email}
            </p>
          )}
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="text-center py-12">
            <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-zinc-400">Loading your gear...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && items.length === 0 && (
          <div className="text-center py-12 bg-zinc-900 rounded-lg">
            <svg className="w-16 h-16 text-zinc-700 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <h3 className="text-lg font-medium text-white mb-2">No gear yet</h3>
            <p className="text-zinc-400 mb-6">
              Add items from kit recommendations to track what you own.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
            >
              Get Kit Recommendations
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </Link>
          </div>
        )}

        {/* Gear list grouped by category */}
        {!isLoading && !error && items.length > 0 && (
          <div className="space-y-8">
            {Object.entries(groupedItems).map(([categoryId, categoryItems]) => (
              <div key={categoryId}>
                <h2 className="text-lg font-medium text-white mb-4 capitalize">
                  {categoryId.replace(/_/g, ' ')}
                </h2>
                <div className="space-y-3">
                  {categoryItems.map(item => (
                    <div
                      key={item.id}
                      className="bg-zinc-900 rounded-lg p-4 flex items-start justify-between gap-4"
                    >
                      <div className="flex-1">
                        <h3 className="font-medium text-white">{item.product_name}</h3>
                        {item.notes && (
                          <p className="text-sm text-zinc-400 mt-1">{item.notes}</p>
                        )}
                        {item.acquired_date && (
                          <p className="text-xs text-zinc-500 mt-1">
                            Acquired: {new Date(item.acquired_date).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRemove(item.id)}
                        disabled={removingId === item.id}
                        className="p-2 text-zinc-500 hover:text-red-400 disabled:opacity-50 transition-colors"
                        title="Remove from gear closet"
                      >
                        {removingId === item.id ? (
                          <div className="w-5 h-5 border-2 border-zinc-500 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Stats */}
            <div className="border-t border-zinc-800 pt-6 mt-8">
              <div className="flex items-center justify-between text-sm text-zinc-400">
                <span>{items.length} items in your gear closet</span>
                <Link
                  to="/"
                  className="text-orange-500 hover:text-orange-400 transition-colors"
                >
                  Get Kit Recommendations
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
